/* eslint-disable no-nested-ternary */
import { TFunction } from 'i18next';
import isString from 'lodash-es/isString';
import merge from 'lodash-es/merge';
import camelCase from 'lodash/camelCase';
import { CSSProperties } from 'react';

import { AtiraLinkBackgroundImagePosition } from '../../../model/atira-link/types/AtiraLinkBackgroundImagePosition';
import { AtiraLinkBackgroundImageSize } from '../../../model/atira-link/types/AtiraLinkBackgroundImageSize';
import { AtiraLinkBorderRadius } from '../../../model/atira-link/types/AtiraLinkButtonBorderRadius';
import { AtiraLinkStyles } from '../../../model/atira-link/types/AtiraLinkStyles';
import { AtiraLinkStylesBackgroundMode } from '../../../model/atira-link/types/AtiraLinkStylesBackgroundMode.enum';
import { getMediaUrl } from '../../../utils/Media';

const getTestId = (value: string, testIdCreator?: ATVoidFunction<string>) => {
  return testIdCreator?.(value.toLowerCase());
};

export const borderRadiusOptions = (
  t: TFunction,
  testIdCreator?: ATVoidFunction<string>,
) => {
  return Object.entries(AtiraLinkBorderRadius).map(([key, value]) => ({
    label: t(`common.${key.toLowerCase()}`),
    value: value,
    'data-testid': getTestId(value.toString(), testIdCreator),
  }));
};

export const borderThicknessOptions = (
  t: TFunction,
  testIdCreator?: ATVoidFunction<string>,
) => {
  return [
    { label: t('common.none'), value: 0 },
    {
      label: t('forms.create.step.6.input.borderThickness.thin'),
      value: 1,
    },
    { label: t('common.medium'), value: 2 },
    {
      label: t('forms.create.step.6.input.borderThickness.thick'),
      value: 4,
    },
  ].map((option) => ({
    ...option,
    'data-testid': getTestId(option.value.toString(), testIdCreator),
  }));
};

export const borderStyleOptions = (
  t: TFunction,
  testIdCreator?: ATVoidFunction<string>,
) => {
  return [
    { label: t('common.none'), value: '' },
    { label: t('common.solid'), value: 'solid' },
    { label: t('common.dashed'), value: 'dashed' },
    { label: t('common.dotted'), value: 'dotted' },
  ].map((option) => ({
    ...option,
    'data-testid': getTestId(option.value, testIdCreator),
  }));
};

export const backgroundImagePositionOptions = (
  t: TFunction,
  testIdCreator?: ATVoidFunction<string>,
) => {
  return Object.values(AtiraLinkBackgroundImagePosition).map((option) => ({
    label: t(
      `atiraLink.create.design.background.image.position.${camelCase(option)}`,
    ),
    value: option,
    'data-testid': getTestId(option, testIdCreator),
  }));
};

export const backgroundImageSizeOptions = (
  t: TFunction,
  testIdCreator?: ATVoidFunction<string>,
) =>
  Object.values(AtiraLinkBackgroundImageSize).map((option) => ({
    label: t(
      `atiraLink.create.design.background.image.size.${camelCase(option)}`,
    ),
    value: option,
    'data-testid': getTestId(option, testIdCreator),
  }));

export const getCustomizableButtonStyles = (
  buttonStyles: AtiraLinkStyles['button'],
): AtiraLinkStyles['button'] => {
  if (!buttonStyles) return {};

  const {
    borderRadius,
    backgroundColor,
    borderColor,
    borderStyle,
    borderWidth,
  } = buttonStyles;

  return {
    ...(borderRadius ? { borderRadius: `${borderRadius}px` } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderColor ? { borderColor } : {}),
    ...(borderStyle ? { borderStyle } : {}),
    ...(borderWidth ? { borderWidth: `${borderWidth}px` } : {}),
  } as AtiraLinkStyles['button'];
};

export const getCustomizableImageStyles = (
  imageStyles: AtiraLinkStyles['image'],
): CSSProperties => {
  if (!imageStyles) return {};

  const { borderStyle, borderRadius, borderWidth, borderColor } = imageStyles;

  return {
    ...(borderStyle ? { borderStyle } : {}),
    ...(borderRadius ? { borderRadius: `${borderRadius}px` } : {}),
    ...(borderWidth ? { borderWidth: `${borderWidth}px` } : {}),
    ...(borderColor ? { borderColor } : {}),
  };
};

export const getCustomizableBackgroundStyles = (styles: AtiraLinkStyles) => {
  if (!styles) {
    return {};
  }

  if (
    styles.background?.backgroundMode === AtiraLinkStylesBackgroundMode.IMAGE &&
    styles.backgroundImage
  ) {
    const img = styles.backgroundImage as File | string;
    return {
      backgroundImage:
        img instanceof File
          ? `url(${URL.createObjectURL(img)})`
          : isString(img)
            ? `url(${getMediaUrl(img)})`
            : undefined,
      backgroundPosition: styles?.background?.backgroundImagePosition,
      backgroundSize: styles?.background?.backgroundImageSize,
    } as CSSProperties;
  }

  return merge(
    { backgroundColor: styles.background?.color },
    styles.background?.color ? { backgroundImage: 'none' } : {},
  ) as CSSProperties;
};
